import React, { useMemo, useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
} from '@mui/material';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender } from '@tanstack/react-table';

import PaginationField from 'components/Pagination';

import { ReactComponent as CellSortUpIcon } from 'assets/images/icons/btn_table_cell_sort_up.svg';
import { ReactComponent as CellSortDownIcon } from 'assets/images/icons/btn_table_cell_sort_down.svg';
import { ReactComponent as CellSortIcon } from 'assets/images/icons/btn_table_cell_sort.svg';

const StyledSortableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'withLeftBorder' && prop !== 'withRightBorder',
})(({ theme, withLeftBorder, withRightBorder }) => ({
  textTransform: 'none',
  '&:not(:last-of-type)::after': {
    display: 'none',
  },
  borderLeft: withLeftBorder
    ? `1px solid ${theme.palette.primary.greyThread}`
    : 'none',
  borderRight: withRightBorder
    ? `1px solid ${theme.palette.primary.greyThread}`
    : 'none',
}));

const DragHandleBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexGrow: 1,
}));

const SortIconBox = styled(Box)(() => ({
  cursor: 'pointer',
  marginLeft: 8,
}));

const PinnedRightBorder = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '1px',
  backgroundColor: theme.palette.primary.greyThread,
  zIndex: 20,
}));

const SortableHeaderCell = ({
  header,
  pinnedColumnIds,
  pinnedLeftStyle,
  children,
  enableSorting,
  enableColumnOrder,
}) => {
  const isSelectColumn = header.id === 'select';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: header.id,
    disabled: isSelectColumn,
  });

  const style = {
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
    ...pinnedLeftStyle
  };

  return (
    <StyledSortableCell
      style={style}
      withLeftBorder={header.column.columnDef.meta?.withLeftBorder}
      withRightBorder={header.column.columnDef.meta?.withRightBorder}
      align={header.column.columnDef.meta?.align ?? 'left'}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <DragHandleBox
          ref={setNodeRef}
          {...(!isSelectColumn && attributes)}
          {...(!isSelectColumn && listeners)}
          sx={{
            cursor:
              enableColumnOrder === false ||
                pinnedColumnIds.includes(header.id) ||
                isSelectColumn
                ? 'default'
                : 'grab',
          }}
        >
          {children}
        </DragHandleBox>
        {enableSorting && header.column.getCanSort() && !isSelectColumn && (
          <SortIconBox onClick={header.column.getToggleSortingHandler()}>
            {header.column.getIsSorted() === 'asc' ? (
              <CellSortUpIcon />
            ) : header.column.getIsSorted() === 'desc' ? (
              <CellSortDownIcon />
            ) : (
              <CellSortIcon />
            )}
          </SortIconBox>
        )}
      </Box>
    </StyledSortableCell>
  );
};

const TableField = ({
  table,
  tableName,
  enableSorting = false,
  enableColumnOrder = false,
  enablePagination = false,
  enableColumnPinning = false,
  pendingChildren,
  drawerOpen,
  handleRowOnClick = {},
  overlayData = [],
  selectedRowId: externalSelectedRowId,
  getIsRowDisplayRemoveLine,
}) => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const activeRowId = externalSelectedRowId || selectedRowId;

  const theme = useTheme();
  const rows = table.getRowModel().rows;
  const dataArray = rows.map((row) => row.original);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const columnOrder = table.getState().columnOrder;
  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const unpinnedColumnOrder = columnOrder.filter((colId) => {
    const col = table.getAllLeafColumns().find((c) => c.id === colId);
    return !col?.getIsPinned();
  });

  const leftPinnedIds = table.getLeftLeafColumns().map((col) => col.id);
  const rightPinnedIds = table.getRightLeafColumns().map((col) => col.id);
  const pinnedColumnIds = [...leftPinnedIds, ...rightPinnedIds];

  // 處理 左側 pinning 不被 X 軸 Scroll bar 影響 (目前不做右側 pinning)
  const leftOffsets = useMemo(() => {
    if (!enableColumnPinning) return {};
    const offsets = {};
    let acc = 0;
    for (const col of leftPinnedIds) {
      const width = 200;
      offsets[col] = acc;
      acc += width;
    }
    return offsets;
  }, [leftPinnedIds]);

  const pinnedLeftStyle = (col, rowIndex, isRowClick, showOverlay) => {
    const width = 200;
    const pin = col.columnDef?.enableColumnPinning;
    if (pin) {
      const left = leftOffsets[col.id] ?? 0;

      let boxShadow = "none";

      if (isRowClick) {
        const keys = Object.keys(leftOffsets);
        const firstKey = keys[0];
        const lastKey = keys[keys.length - 1];
        const rowClickBorderColor = theme.palette.primary.greenPrimary;

        if (col.id === firstKey) {
          boxShadow = `inset 2px 0 0 0 ${rowClickBorderColor}, inset 0 2px 0 0 ${rowClickBorderColor}, inset 0 -2px 0 0 ${rowClickBorderColor}`;
        } else if (col.id === lastKey) {
          boxShadow = `inset 0 2px 0 0 ${rowClickBorderColor}, inset 0 -2px 0 0 ${rowClickBorderColor}`;
        } else {
          boxShadow = `inset 0 2px 0 0 ${rowClickBorderColor}, inset 0 -2px 0 0 ${rowClickBorderColor}`;
        }
      }

      return {
        minWidth: width,
        maxWidth: width,
        width,
        position: "sticky",
        left: left,
        zIndex: 3,
        boxShadow,
        backgroundColor:
          rowIndex % 2 === 1
            ? (showOverlay && theme.palette.primary.whiteSecoundTransparency) || theme.palette.primary.whiteSecound
            : theme.palette.primary.white,
      };
    }

    return { minWidth: width, maxWidth: width, width };
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(active.id);
    const newIndex = columnOrder.indexOf(over.id);
    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
    table.setColumnOrder(newOrder);
  }

  function getJustifyContentByAlign(align) {
    switch (align) {
      case 'center':
        return 'center';
      case 'right':
        return 'flex-end';
      case 'left':
      default:
        return 'flex-start';
    }
  }

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedRowId(null);
    }
  }, [drawerOpen]);

  return (
    <>
      <TableContainer>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <Table>
            <TableHead>
              <SortableContext
                items={unpinnedColumnOrder}
                strategy={horizontalListSortingStrategy}
              >
                <TableRow sx={{ backgroundColor: theme.palette.primary.white }}>
                  {headers.map((header) => (
                    <SortableHeaderCell
                      key={header.id}
                      header={header}
                      pinnedColumnIds={pinnedColumnIds}
                      pinnedLeftStyle={pinnedLeftStyle(header.column, null)}
                      enableColumnOrder={enableColumnOrder}
                      enableSorting={enableSorting}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent:
                            header.column.getCanSort() && enableSorting
                              ? 'space-between'
                              : getJustifyContentByAlign(
                                header.column.columnDef.meta?.align
                              ),
                          alignItems: 'center',
                          width: '100%',
                          gap: 8,
                          fontWeight: theme.typography.customFontWeight[2],
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.columnDef.enableColumnPinning
                          && (header.column.id === leftPinnedIds[leftPinnedIds.length - 1]) && (
                            <PinnedRightBorder />
                          )}
                      </Box>
                    </SortableHeaderCell>
                  ))}
                </TableRow>
              </SortableContext>
            </TableHead>
            {dataArray.length === 0 ? (
              <TableBody>
                {typeof pendingChildren === 'function'
                  ? pendingChildren({ headers, table })
                  : (
                    <TableRow>
                      <TableCell colSpan={headers.length} align="center">
                        {pendingChildren}
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            ) : (
              <TableBody>
                {rows.map((row, rowIndex) => {
                  const isRowDisplayRemoveLine = typeof getIsRowDisplayRemoveLine === 'function' ? getIsRowDisplayRemoveLine(row) : false;
                  const isRowClick = !isRowDisplayRemoveLine && typeof handleRowOnClick === 'function' && row.id === selectedRowId;
                  const showOverlay = overlayData.length > 0 ? overlayData.some((key) => row.original[key] === true) : null;

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        position: "relative",
                        backgroundColor:
                          rowIndex % 2 === 1
                            ? (showOverlay && theme.palette.primary.whiteSecoundTransparency) || theme.palette.primary.whiteSecound
                            : theme.palette.primary.white,
                        cursor: isRowDisplayRemoveLine ? "default" : ( typeof handleRowOnClick === 'function' ? "pointer" : "default"),
                      }}
                      onClick={e => {
                        if (isRowDisplayRemoveLine || typeof handleRowOnClick !== 'function') {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }

                        setSelectedRowId(row.id);

                        const formatCurrentData = {
                          id: row.id,
                          ...row.original,
                        };

                        handleRowOnClick(formatCurrentData);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          sx={{
                            position: isRowDisplayRemoveLine ? 'relative' : 'initial',
                            '&::after': isRowDisplayRemoveLine ? {
                              content: '""',
                              position: 'absolute',
                              top: '50%',
                              left: 0,
                              right: 0,
                              height: '1px',
                              backgroundColor: theme.palette.primary.black,
                              zIndex: 10,
                              pointerEvents: 'none',
                            } : {},
                            ...(showOverlay && { color: theme.palette.primary.greyPrimaryTransparencySecound }),
                            borderLeft:
                              !enableColumnPinning &&
                                (cell.column.columnDef.meta?.withLeftBorder)
                                ? `1px solid ${theme.palette.primary.greyThread}`
                                : 'none',
                            borderRight:
                              !enableColumnPinning && cell.column.columnDef.meta?.withRightBorder
                                ? `1px solid ${theme.palette.primary.greyThread}`
                                : 'none',
                            ...pinnedLeftStyle(cell.column, rowIndex, isRowClick, showOverlay)
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          {cell.column.columnDef.enableColumnPinning
                            && cell.column.id === leftPinnedIds[leftPinnedIds.length - 1] && (
                              <PinnedRightBorder />
                            )}
                          {isRowClick && !cell.column.columnDef?.enableColumnPinning && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                boxShadow: `0 0 0 2px ${theme.palette.primary.greenPrimary} inset`,
                                pointerEvents: "none",
                                zIndex: 3,
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            )}
          </Table>
        </DndContext>
      </TableContainer>
      {enablePagination && (
        <Box mt={2} display="flex" justifyContent="center">
          <PaginationField
            name={tableName}
            pageCount={table.getPageCount()}
            pageIndex={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            onPageChange={(page) => table.setPageIndex(page)}
            onPageSizeChange={(size) => table.setPageSize(size)}
          />
        </Box>
      )}
    </>
  );
};

export default TableField;
